import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ListItem from '@mui/material/ListItem';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { Channel } from '../../api';
import { StatusChip } from '../../components/StatusChip';
import { reasonMessage } from '../../i18n';
import { ChannelPhotoAvatar } from '../onboarding';

// dir="auto": a channel name in Arabic or Hebrew reads right to left (C3 R7). A constant, not a text.
const OWN_DIRECTION = 'auto';

/** Props of {@link OtherChannelRow}. */
export interface OtherChannelRowProps {
  readonly channel: Channel;
  /** A copy of this channel is in flight (S9 R6, AC16). */
  readonly busy: boolean;
  readonly onCopy: (channel: Channel) => void;
}

/**
 * A row of "Other channels" (S9 R6; `GestionCanaux.html`): name, the affiliate label, and
 * "Copy", named "Copy {name}" (label in name, WCAG 2.5.3). A channel with no subscription and no
 * access (not returned by the API today) shows E3's inactive chip and text instead, with no button.
 * No statistic (FXM-01 §8).
 */
export function OtherChannelRow({ channel, busy, onCopy }: OtherChannelRowProps): ReactElement {
  const { t } = useTranslation();
  const inactive = reasonMessage('CHANNEL_INACCESSIBLE');
  const detail = !channel.accessible
    ? t(inactive.key, inactive.values)
    : channel.recommendedByAffiliate
      ? t('onboarding.channels.affiliateChannel')
      : null;
  return (
    <ListItem sx={{ gap: 1.5, py: 1.25, px: 1.75 }}>
      <ChannelPhotoAvatar channel={channel} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          component="span"
          dir={OWN_DIRECTION}
          sx={{
            display: 'block',
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {channel.title}
        </Box>
        {detail === null ? null : (
          <Box
            component="span"
            sx={(theme) => ({ display: 'block', fontSize: 13, color: theme.fx.textMuted })}
          >
            {detail}
          </Box>
        )}
      </Box>
      {channel.accessible ? (
        <Button
          variant="outlined"
          size="small"
          disabled={busy}
          aria-busy={busy}
          aria-label={t('onboarding.channels.copy', { name: channel.title })}
          onClick={() => {
            onCopy(channel);
          }}
          sx={{ minHeight: 36, flexShrink: 0 }}
        >
          {t('channels.copyButton')}
        </Button>
      ) : (
        <StatusChip tone="danger">{t('onboarding.channels.inactive')}</StatusChip>
      )}
    </ListItem>
  );
}
